/**
 * Room management utilities and derived state computations.
 * Framework-agnostic — pure functions operating on RoomState.
 */
// ─── Derived State ──────────────────────────────────────────────────
/**
 * Get room readiness info.
 */
export function getRoomReadiness(state) {
    const totalPlayers = state.players.length;
    const readyPlayers = state.players.filter((p) => p.isReady).length;
    const humanPlayers = state.players.filter((p) => !p.isAI).length;
    const aiPlayers = state.players.filter((p) => p.isAI).length;
    const isFull = totalPlayers >= state.maxPlayers;
    const allReady = totalPlayers > 0 && readyPlayers === totalPlayers;
    return {
        totalPlayers,
        readyPlayers,
        humanPlayers,
        aiPlayers,
        isFull,
        allReady,
        canStart: isFull && allReady,
    };
}
/**
 * Get local player from room state.
 */
export function getLocalRoomPlayer(state) {
    if (!state.localPlayerId)
        return null;
    return state.players.find((p) => p.id === state.localPlayerId) ?? null;
}
/**
 * Whether the local player is the host.
 */
export function isLocalPlayerHost(state) {
    const local = getLocalRoomPlayer(state);
    return local?.isHost ?? false;
}
/**
 * Get enriched player info for lobby display.
 */
export function getLobbyPlayerInfos(state) {
    const isHost = isLocalPlayerHost(state);
    return state.players.map((player) => ({
        player,
        isLocal: player.id === state.localPlayerId,
        canKick: isHost && player.id !== state.localPlayerId,
        canToggleReady: player.id === state.localPlayerId && !player.isAI,
    }));
}
/**
 * Get the number of open slots in the room.
 */
export function getOpenSlots(state) {
    return Math.max(0, state.maxPlayers - state.players.length);
}
/**
 * Whether the local player can add an AI player.
 */
export function canAddAI(state) {
    return isLocalPlayerHost(state) && state.players.length < state.maxPlayers;
}
/**
 * Format room phase for display.
 */
export function getRoomPhaseDisplay(state) {
    switch (state.phase) {
        case 'idle':
            return { label: 'Not in a room', color: 'gray' };
        case 'creating':
            return { label: 'Creating room...', color: 'yellow' };
        case 'joining':
            return { label: 'Joining room...', color: 'yellow' };
        case 'lobby':
            return { label: 'In Lobby', color: 'green' };
        case 'starting':
            return { label: 'Starting game...', color: 'blue' };
    }
}
//# sourceMappingURL=useRoom.js.map